<template>
  <section
    v-if="showSection"
    class="gallery-section"
    :class="`gallery-${galleryStyle}`"
  >
    <div class="container-fluid">
      <!-- Empty State -->
      <div v-if="galleryImages.length === 0" class="text-center py-5">
        <div class="empty-gallery-state">
          <i class="bi bi-images" style="font-size: 4rem; color: #ccc"></i>
          <h4 class="mt-3 text-muted">Δεν υπάρχουν εικόνες στη γκαλερί</h4>
        </div>
      </div>

      <!-- Gallery Content -->
      <template v-else>
        <!-- Modern Gallery Style (Default) -->
        <template v-if="galleryStyle === 'modern'">
          <!-- Main Image Display -->
          <div class="row mb-2">
            <div class="col-12">
              <div class="main-image-container">
                <img
                  :src="getImageUrl(currentMainImage)"
                  :alt="currentMainImage?.alt"
                  class="main-gallery-image"
                  loading="eager"
                  decoding="async"
                  @click="$emit('openLightbox', currentMainImageIndex)"
                />
                <div class="main-image-overlay">
                  <div class="main-overlay-content">
                    <h3 class="text-white mb-2">{{ currentMainImage?.title }}</h3>
                    <p class="text-white-50 mb-0">{{ currentMainImage?.description }}</p>
                    <span class="badge bg-primary mt-2">{{ currentMainImage?.category }}</span>
                  </div>
                  <button
                    class="main-image-expand"
                    @click="$emit('openLightbox', currentMainImageIndex)"
                  >
                    <i class="bi bi-arrows-fullscreen"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Thumbnail Navigation -->
          <div class="row">
            <div class="col-12">
              <div class="thumbnail-container">
                <div class="thumbnail-scroll">
                  <div
                    v-for="(image, index) in filteredImages"
                    :key="index"
                    class="thumbnail-item"
                    :class="{ active: index === currentMainImageIndex }"
                    @click="setMainImage(index)"
                  >
                    <img
                      :src="getImageUrl(image)"
                      :alt="image.alt"
                      class="thumbnail-image"
                      loading="lazy"
                      decoding="async"
                    />
                    <div class="thumbnail-overlay">
                      <i class="bi bi-eye text-white"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Grid Gallery Style -->
        <template v-else-if="galleryStyle === 'grid'">
          <div class="row g-3">
            <div
              v-for="(image, index) in filteredImages.slice(0, 12)"
              :key="index"
              class="col-lg-3 col-md-4 col-sm-6"
            >
              <div class="grid-image-item" @click="$emit('openLightbox', index)">
                <img
                  :src="getImageUrl(image)"
                  :alt="image.alt"
                  class="grid-image"
                  loading="lazy"
                  decoding="async"
                />
                <div class="grid-overlay">
                  <div class="grid-content">
                    <h6 class="text-white">{{ image.title }}</h6>
                    <i class="bi bi-zoom-in text-white"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Hexagon (Κυψέλη) Gallery Style -->
        <template v-else-if="galleryStyle === 'hexagon'">
          <div class="hexagon-gallery-container">
            <div class="hexagon-grid">
              <div
                v-for="(image, index) in filteredImages"
                :key="index"
                class="hexagon-wrapper"
                @click="$emit('openLightbox', index)"
              >
                <div class="hexagon-cell">
                  <div class="hexagon-content">
                    <img
                      :src="getImageUrl(image)"
                      :alt="image.alt"
                      class="hexagon-image"
                      loading="lazy"
                      decoding="async"
                    />
                    <div class="hexagon-overlay">
                      <div class="hexagon-info">
                        <h5>{{ image.title }}</h5>
                        <p>{{ image.description }}</p>
                        <span class="hexagon-badge">{{ image.category }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Parallax Depth Gallery Style -->
        <template v-else-if="galleryStyle === 'parallax'">
          <div class="parallax-gallery">
            <div class="parallax-scene">
              <div
                v-for="(image, index) in filteredImages"
                :key="index"
                class="parallax-layer"
                :style="{ zIndex: filteredImages.length - index }"
                @click="$emit('openLightbox', index)"
              >
                <div class="parallax-card">
                  <img
                    :src="getImageUrl(image)"
                    :alt="image.alt"
                    class="parallax-image"
                    loading="lazy"
                  />
                  <div class="parallax-shadow"></div>
                  <div class="parallax-info">
                    <h4>{{ image.title }}</h4>
                    <p>{{ image.description }}</p>
                    <span class="parallax-tag">{{ image.category }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Neon Glow Gallery Style -->
        <template v-else-if="galleryStyle === 'neon'">
          <div class="neon-gallery">
            <div class="neon-grid">
              <div
                v-for="(image, index) in filteredImages"
                :key="index"
                class="neon-item"
                @click="$emit('openLightbox', index)"
              >
                <div class="neon-border"></div>
                <div class="neon-content">
                  <img
                    :src="getImageUrl(image)"
                    :alt="image.alt"
                    class="neon-image"
                    loading="lazy"
                  />
                  <div class="neon-overlay">
                    <h5 class="neon-title">{{ image.title }}</h5>
                    <p class="neon-description">{{ image.description }}</p>
                    <span class="neon-badge">{{ image.category }}</span>
                  </div>
                </div>
                <div class="neon-glow"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Liquid Morph Gallery Style -->
        <template v-else-if="galleryStyle === 'liquid'">
          <div class="liquid-gallery">
            <div class="liquid-carousel-wrapper">
              <!-- Navigation Buttons -->
              <button
                v-if="canScrollLiquidPrev"
                class="liquid-nav liquid-nav-prev"
                @click="prevLiquidSlide"
                aria-label="Previous"
              >
                <i class="bi bi-chevron-left"></i>
              </button>

              <button
                v-if="canScrollLiquidNext"
                class="liquid-nav liquid-nav-next"
                @click="nextLiquidSlide"
                aria-label="Next"
              >
                <i class="bi bi-chevron-right"></i>
              </button>

              <!-- Visible Images Container -->
              <div class="liquid-container">
                <div
                  v-for="(image, index) in visibleLiquidImages"
                  :key="liquidCarouselIndex + index"
                  class="liquid-blob"
                  @click="$emit('openLightbox', liquidCarouselIndex + index)"
                >
                  <div class="liquid-shape">
                    <img
                      :src="getImageUrl(image)"
                      :alt="image.alt"
                      class="liquid-image"
                      loading="lazy"
                    />
                    <div class="liquid-overlay">
                      <div class="liquid-text">
                        <h5>{{ image.title }}</h5>
                        <p>{{ image.description }}</p>
                        <span class="liquid-pill">{{ image.category }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Carousel Indicators -->
              <div v-if="filteredImages.length > 4" class="liquid-indicators">
                <span
                  v-for="i in Math.ceil(filteredImages.length / 4)"
                  :key="i"
                  class="liquid-indicator"
                  :class="{ active: Math.floor(liquidCarouselIndex / 4) === i - 1 }"
                  @click="liquidCarouselIndex = (i - 1) * 4"
                ></span>
              </div>
            </div>
          </div>
        </template>

        <!-- Isometric 3D Gallery Style -->
        <template v-else-if="galleryStyle === 'isometric'">
          <div class="isometric-gallery">
            <div class="isometric-grid">
              <div
                v-for="(image, index) in filteredImages"
                :key="index"
                class="isometric-cube"
                @click="$emit('openLightbox', index)"
              >
                <div class="cube-scene">
                  <div class="cube-wrapper">
                    <div class="cube-face cube-front">
                      <img :src="getImageUrl(image)" :alt="image.alt" />
                    </div>
                    <div class="cube-face cube-top">
                      <div class="cube-top-content">
                        <h6>{{ image.title }}</h6>
                      </div>
                    </div>
                    <div class="cube-face cube-right">
                      <div class="cube-side-content">
                        <span>{{ image.category }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Holographic Gallery Style -->
        <template v-else-if="galleryStyle === 'holographic'">
          <div class="holographic-gallery">
            <div class="holographic-carousel-wrapper">
              <!-- Main Large Image (50%) -->
              <div class="holographic-main" @click="$emit('openLightbox', holographicMainIndex)">
                <div class="holographic-panel holographic-main-panel">
                  <div class="holographic-scanlines"></div>
                  <img
                    :src="getImageUrl(filteredImages[holographicMainIndex])"
                    :alt="filteredImages[holographicMainIndex]?.alt"
                    class="holographic-image"
                    loading="eager"
                  />
                  <div class="holographic-glitch"></div>
                  <div class="holographic-overlay">
                    <div class="holographic-data">
                      <h5 class="holographic-title">
                        {{ filteredImages[holographicMainIndex]?.title }}
                      </h5>
                      <p class="holographic-desc">
                        {{ filteredImages[holographicMainIndex]?.description }}
                      </p>
                      <span class="holographic-tag">{{
                        filteredImages[holographicMainIndex]?.category
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Side Grid (4 images 2x2) -->
              <div class="holographic-grid-wrapper">
                <!-- Navigation Buttons -->
                <button
                  v-if="canScrollHolographicPrev"
                  class="holographic-nav holographic-nav-prev"
                  @click="prevHolographicSlide"
                  aria-label="Previous"
                >
                  <i class="bi bi-chevron-left"></i>
                </button>

                <button
                  v-if="canScrollHolographicNext"
                  class="holographic-nav holographic-nav-next"
                  @click="nextHolographicSlide"
                  aria-label="Next"
                >
                  <i class="bi bi-chevron-right"></i>
                </button>

                <!-- 2x2 Grid -->
                <div class="holographic-grid">
                  <div
                    v-for="(image, index) in visibleHolographicThumbs"
                    :key="holographicCarouselIndex + index"
                    class="holographic-frame"
                    @click="setHolographicMain(holographicCarouselIndex + index + 1)"
                  >
                    <div class="holographic-panel">
                      <div class="holographic-scanlines"></div>
                      <img
                        :src="getImageUrl(image)"
                        :alt="image.alt"
                        class="holographic-image"
                        loading="lazy"
                      />
                      <div class="holographic-glitch"></div>
                      <div class="holographic-thumb-overlay">
                        <i class="bi bi-arrows-fullscreen"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Carousel Indicators -->
                <div v-if="filteredImages.length > 5" class="holographic-indicators">
                  <span
                    v-for="i in Math.ceil((filteredImages.length - 1) / 4)"
                    :key="i"
                    class="holographic-indicator"
                    :class="{ active: Math.floor(holographicCarouselIndex / 4) === i - 1 }"
                    @click="holographicCarouselIndex = (i - 1) * 4"
                  ></span>
                </div>
              </div>
            </div>
          </div>
        </template>
 </template
      ><!-- End v-else -->
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { IMAGE_BASE_URL } from '@/config/env'

interface GalleryImage {
  filename: string
  alt: string
  title: string
  description: string
  category: string
  source_folder?: string
}

interface Props {
  showSection: boolean
  galleryImages: GalleryImage[]
  galleryStyle?: string
  hasMoreImages?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  galleryStyle: 'modern',
  hasMoreImages: false,
})

// Debug logging
watch(
  () => props.galleryImages,
  (newVal) => {},
  { immediate: true },
)

defineEmits<{
  openLightbox: [index: number]
  loadMore: []
}>()

const currentMainImageIndex = ref(0)
const liquidCarouselIndex = ref(0)
const holographicMainIndex = ref(0)
const holographicCarouselIndex = ref(0)

const filteredImages = computed(() => {
  return props.galleryImages
})

const currentMainImage = computed(() => {
  const image = filteredImages.value[currentMainImageIndex.value]
  return image
})

const setMainImage = (index: number) => {
  currentMainImageIndex.value = index
}

// Liquid carousel navigation
const visibleLiquidImages = computed(() => {
  const start = liquidCarouselIndex.value
  const end = start + 4
  return filteredImages.value.slice(start, end)
})

const nextLiquidSlide = () => {
  if (liquidCarouselIndex.value + 4 < filteredImages.value.length) {
    liquidCarouselIndex.value++
  } else {
    liquidCarouselIndex.value = 0 // Loop back to start
  }
}

const prevLiquidSlide = () => {
  if (liquidCarouselIndex.value > 0) {
    liquidCarouselIndex.value--
  } else {
    liquidCarouselIndex.value = Math.max(0, filteredImages.value.length - 4) // Go to last set
  }
}

const canScrollLiquidPrev = computed(() => liquidCarouselIndex.value > 0)
const canScrollLiquidNext = computed(
  () => liquidCarouselIndex.value + 4 < filteredImages.value.length,
)

// Holographic carousel navigation
const visibleHolographicThumbs = computed(() => {
  const start = holographicCarouselIndex.value + 1 // Skip first image (it's the main)
  const end = start + 4
  return filteredImages.value.slice(start, end)
})

const setHolographicMain = (index: number) => {
  holographicMainIndex.value = index
}

const nextHolographicSlide = () => {
  if (holographicCarouselIndex.value + 5 < filteredImages.value.length) {
    holographicCarouselIndex.value += 4
  } else {
    holographicCarouselIndex.value = 0 // Loop back to start
  }
}

const prevHolographicSlide = () => {
  if (holographicCarouselIndex.value >= 4) {
    holographicCarouselIndex.value -= 4
  } else {
    holographicCarouselIndex.value = Math.max(
      0,
      Math.floor((filteredImages.value.length - 5) / 4) * 4,
    )
  }
}

const canScrollHolographicPrev = computed(() => holographicCarouselIndex.value > 0)
const canScrollHolographicNext = computed(
  () => holographicCarouselIndex.value + 5 < filteredImages.value.length,
)

// Get correct image URL based on source_folder
const getImageUrl = (image: GalleryImage) => {
  if (!image || !image.filename) return ''

  // If it's already a full URL, return as is
  if (image.filename.startsWith('http')) {
    return image.filename
  }

  // Use source_folder if available
  const folder = image.source_folder || 'gallery'
  return `/backend/uploads/${folder}/${image.filename}`
}
</script>

<style scoped>
/* Gallery Section — body --navbar-offset clears fixed navbar; no extra top gap */
.gallery-section {
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 1.25rem;
}

@media (max-width: 768px) {
  .gallery-section {
    padding-top: 0;
    padding-bottom: 0.75rem;
  }
}

/* Ensure GIF animations play on all devices */
img[src$=".gif"],
img[src*=".gif?"] {
  image-rendering: auto !important;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  will-change: auto;
}

/* Main Image Display */
.main-image-container {
  position: relative;
  width: 100%;
  height: 600px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.main-gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
  content-visibility: auto;
}

.main-image-container:hover .main-gallery-image {
  transform: scale(1.05);
}

.main-image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.main-overlay-content {
  flex: 1;
}

.main-image-expand {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: none;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.25rem;
}

.main-image-expand:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Thumbnail Navigation */
.thumbnail-container {
  margin-top: 1rem;
  overflow: hidden;
}

.thumbnail-scroll {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.5rem 0;
  scroll-behavior: smooth;
}

.thumbnail-scroll::-webkit-scrollbar {
  height: 6px;
}

.thumbnail-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.thumbnail-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
}

.thumbnail-item {
  position: relative;
  min-width: 120px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid transparent;
  transition: all 0.3s ease;
}

.thumbnail-item.active {
  border-color: #0d6efd;
  transform: scale(1.05);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.thumbnail-item:hover .thumbnail-overlay {
  opacity: 1;
}

/* Grid Gallery Style */
.grid-image-item {
  position: relative;
  height: 250px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.grid-image-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.grid-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.grid-image-item:hover .grid-image {
  transform: scale(1.1);
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.grid-image-item:hover .grid-overlay {
  opacity: 1;
}

.grid-content {
  text-align: center;
}

.grid-content h6 {
  margin-bottom: 0.5rem;
}

.grid-content i {
  font-size: 2rem;
}

/* ============================================
   HEXAGON (ΚΥΨΕΛΗ) GALLERY STYLE
   ============================================ */
.hexagon-gallery-container {
  width: 100%;
  max-height: 600px; /* Ίδιο ύψος με το modern style */
  overflow: hidden;
  position: relative;
}

.hexagon-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 2rem 0;
}

.hexagon-wrapper {
  width: 160px; /* Μέγεθος προσαρμοσμένο στο modern */
  height: 185px;
  margin: 0 -8px; /* Overlap για honeycomb effect */
  cursor: pointer;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.hexagon-wrapper:nth-child(even) {
  margin-top: 47px; /* Μισό ύψος για offset pattern */
}

.hexagon-cell {
  width: 100%;
  height: 100%;
  position: relative;
  /* ensure clip-path works on WebKit (Safari) */
  -webkit-clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  overflow: hidden;
  background: transparent !important;
}

.hexagon-wrapper:hover .hexagon-cell {
  /* expand into a rounded square on hover; include WebKit prefixed property */
  -webkit-clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 100%, 0% 0%);
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 100%, 0% 0%); /* Γίνεται τετράγωνο */
  transform: scale(1.8) translateZ(50px);
  z-index: 100;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border-radius: 12px;
}

.hexagon-content {
  width: 100%;
  height: 100%;
  position: relative;
}

.hexagon-image {
  /* absolutely position image to fully fill the clipped container and avoid gaps/fallback bg */
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  display: block;
  /* also clip the image itself for extra compatibility */
  -webkit-clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
}

.hexagon-wrapper:hover .hexagon-image {
  transform: scale(1.1);
}

.hexagon-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.95), rgba(108, 117, 125, 0.95));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.hexagon-wrapper:hover .hexagon-overlay {
  opacity: 1;
}

.hexagon-info {
  text-align: center;
  color: white;
  transform: translateY(20px);
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.hexagon-wrapper:hover .hexagon-info {
  transform: translateY(0);
}

.hexagon-info h5 {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.hexagon-info p {
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  opacity: 0.95;
  line-height: 1.4;
}

.hexagon-badge {
  display: inline-block;
  padding: 0.4rem 0.8rem;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Responsive */
@media (max-width: 768px) {
  .main-image-container {
    height: 400px;
  }

  .thumbnail-item {
    min-width: 100px;
    height: 70px;
  }

  .grid-image-item {
    height: 200px;
  }

  /* Hexagon responsive */
  .hexagon-wrapper {
    width: 120px;
    height: 138px;
    margin: 0 -6px;
  }

  .hexagon-wrapper:nth-child(even) {
    margin-top: 35px;
  }

  .hexagon-wrapper:hover .hexagon-cell {
    transform: scale(1.5);
  }

  .hexagon-info h5 {
    font-size: 1rem;
  }

  .hexagon-info p {
    font-size: 0.8rem;
  }
}

@media (max-width: 576px) {
  .hexagon-wrapper {
    width: 100px;
    height: 115px;
    margin: 0 -5px;
  }

  .hexagon-wrapper:nth-child(even) {
    margin-top: 29px;
  }

  .hexagon-wrapper:hover .hexagon-cell {
    transform: scale(1.3);
  }
}

/* ============================================
   PARALLAX DEPTH GALLERY STYLE
   ============================================ */
.parallax-gallery {
  width: 100%;
  max-height: 650px;
  overflow: hidden;
  perspective: 1000px;
  position: relative;
}

.parallax-scene {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  padding: 2rem;
  transform-style: preserve-3d;
}

.parallax-layer {
  width: calc(33.333% - 1.5rem);
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.parallax-layer:hover {
  transform: translateZ(100px) scale(1.1);
  z-index: 1000 !important;
}

.parallax-card {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  transform-style: preserve-3d;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
}

.parallax-image {
  width: 100%;
  height: 350px;
  object-fit: cover;
  transition: transform 0.6s ease;
  filter: brightness(0.9);
}

.parallax-layer:hover .parallax-image {
  transform: scale(1.15);
  filter: brightness(1.1);
}

.parallax-shadow {
  position: absolute;
  bottom: -50px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  height: 50px;
  background: radial-gradient(ellipse, rgba(0, 0, 0, 0.6), transparent);
  filter: blur(20px);
  transition: all 0.6s ease;
}

.parallax-layer:hover .parallax-shadow {
  bottom: -80px;
  width: 100%;
  opacity: 0.8;
}

.parallax-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.95), transparent);
  color: white;
  transform: translateY(100%);
  transition: transform 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.parallax-layer:hover .parallax-info {
  transform: translateY(0);
}

.parallax-info h4 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.parallax-tag {
  display: inline-block;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 25px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* ============================================
   NEON GLOW GALLERY STYLE
   ============================================ */
.neon-gallery {
  padding: 2rem 0;
}

.neon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.neon-item {
  position: relative;
  cursor: pointer;
  border-radius: 15px;
  overflow: hidden;
  transition: transform 0.4s ease;
}

.neon-item:hover {
  transform: translateY(-10px) scale(1.05);
}

.neon-border {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 3px solid transparent;
  border-radius: 15px;
  background: linear-gradient(45deg, #00f3ff, #ff00ff, #00ff88, #ffaa00) border-box;
  -webkit-mask:
    linear-gradient(#fff 0 0) padding-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) padding-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  opacity: 0;
  transition: opacity 0.4s ease;
  animation: neonRotate 3s linear infinite;
}

@keyframes neonRotate {
  0% {
    filter: hue-rotate(0deg);
  }
  100% {
    filter: hue-rotate(360deg);
  }
}

.neon-item:hover .neon-border {
  opacity: 1;
}

.neon-content {
  position: relative;
  height: 350px;
  background: #1a1f3a;
  border-radius: 15px;
  overflow: hidden;
}

.neon-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(1.2) contrast(1.1);
  transition: filter 0.4s ease;
}

.neon-item:hover .neon-image {
  filter: saturate(1.5) contrast(1.2) brightness(1.1);
}

.neon-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 243, 255, 0.3), rgba(255, 0, 255, 0.3));
  opacity: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  transition: opacity 0.4s ease;
}

.neon-item:hover .neon-overlay {
  opacity: 1;
}

.neon-title {
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  text-shadow:
    0 0 10px #00f3ff,
    0 0 20px #00f3ff,
    0 0 30px #00f3ff;
  margin-bottom: 0.5rem;
}

.neon-description {
  color: #e0e0e0;
  font-size: 0.95rem;
  margin-bottom: 1rem;
}

.neon-badge {
  display: inline-block;
  padding: 0.5rem 1.5rem;
  background: rgba(0, 243, 255, 0.2);
  border: 2px solid #00f3ff;
  border-radius: 25px;
  color: #00f3ff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  box-shadow:
    0 0 10px #00f3ff,
    inset 0 0 10px rgba(0, 243, 255, 0.2);
  animation: neonPulse 2s ease-in-out infinite;
}

@keyframes neonPulse {
  0%,
  100% {
    box-shadow:
      0 0 10px #00f3ff,
      inset 0 0 10px rgba(0, 243, 255, 0.2);
  }
  50% {
    box-shadow:
      0 0 20px #00f3ff,
      inset 0 0 15px rgba(0, 243, 255, 0.4);
  }
}

.neon-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(0, 243, 255, 0.3), transparent 70%);
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
}

.neon-item:hover .neon-glow {
  opacity: 1;
}

/* ============================================
   LIQUID MORPH GALLERY STYLE (CAROUSEL)
   ============================================ */
.liquid-gallery {
  padding: 2rem 0;
}

.liquid-carousel-wrapper {
  position: relative;
  padding: 0 60px;
}

.liquid-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  justify-items: center;
}

.liquid-blob {
  width: 100%;
  max-width: 300px;
  height: 350px;
  cursor: pointer;
  transition: all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.liquid-blob:hover {
  transform: scale(1.15) rotate(5deg);
  z-index: 10;
}

/* Navigation Buttons */
.liquid-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(88, 101, 242, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.liquid-nav:hover {
  background: rgba(88, 101, 242, 0.9);
  border-color: rgba(88, 101, 242, 1);
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 20px rgba(88, 101, 242, 0.4);
}

.liquid-nav:hover i {
  color: white;
}

.liquid-nav-prev {
  left: 0;
}

.liquid-nav-next {
  right: 0;
}

.liquid-nav i {
  font-size: 1.5rem;
  color: rgba(88, 101, 242, 0.8);
  transition: color 0.3s ease;
}

/* Carousel Indicators */
.liquid-indicators {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 2rem;
}

.liquid-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(88, 101, 242, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.liquid-indicator:hover {
  background: rgba(88, 101, 242, 0.6);
  transform: scale(1.2);
}

.liquid-indicator.active {
  background: rgba(88, 101, 242, 1);
  transform: scale(1.3);
}

.liquid-shape {
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  overflow: hidden;
  animation: liquidMorph 10s ease-in-out infinite;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: border-radius 0.8s ease;
}

@keyframes liquidMorph {
  0%,
  100% {
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  }
  25% {
    border-radius: 58% 42% 75% 25% / 76% 46% 54% 24%;
  }
  50% {
    border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%;
  }
  75% {
    border-radius: 33% 67% 58% 42% / 63% 68% 32% 37%;
  }
}

.liquid-blob:hover .liquid-shape {
  border-radius: 20% 80% 20% 80% / 20% 80% 20% 80%;
  animation: liquidMorphHover 3s ease-in-out infinite;
}

@keyframes liquidMorphHover {
  0%,
  100% {
    border-radius: 20% 80% 20% 80% / 20% 80% 20% 80%;
  }
  50% {
    border-radius: 80% 20% 80% 20% / 80% 20% 80% 20%;
  }
}

.liquid-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition:
    transform 0.8s ease,
    filter 0.4s ease;
  filter: brightness(0.9);
}

.liquid-blob:hover .liquid-image {
  transform: scale(1.2);
  filter: brightness(1.1) saturate(1.3);
}

.liquid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(88, 101, 242, 0.9), rgba(167, 71, 245, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.5s ease;
  padding: 2rem;
}

.liquid-blob:hover .liquid-overlay {
  opacity: 1;
}

.liquid-text {
  text-align: center;
  color: white;
  transform: scale(0.8);
  transition: transform 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.liquid-blob:hover .liquid-text {
  transform: scale(1);
}

.liquid-text h5 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.liquid-pill {
  display: inline-block;
  padding: 0.5rem 1.5rem;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

/* ============================================
   ISOMETRIC 3D GALLERY STYLE
   ============================================ */
.isometric-gallery {
  padding: 3rem 0;
  perspective: 1500px;
}

.isometric-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 3rem;
  justify-content: center;
}

.isometric-cube {
  width: 280px;
  height: 280px;
  cursor: pointer;
}

.cube-scene {
  width: 100%;
  height: 100%;
  perspective: 1000px;
}

.cube-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transform: rotateX(-15deg) rotateY(25deg);
  transition: transform 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.isometric-cube:hover .cube-wrapper {
  transform: rotateX(-20deg) rotateY(35deg) scale(1.1);
}

.cube-face {
  position: absolute;
  width: 280px;
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.cube-front {
  transform: translateZ(140px);
  background: #1a1a1a;
}

.cube-front img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.isometric-cube:hover .cube-front img {
  transform: scale(1.2);
}

.cube-top {
  transform: rotateX(90deg) translateZ(140px);
  background: linear-gradient(135deg, #0C5A85, #0a4563);
}

.cube-top-content {
  color: white;
  font-weight: 700;
  font-size: 1.2rem;
  text-align: center;
  padding: 1rem;
}

.cube-right {
  transform: rotateY(90deg) translateZ(140px);
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.cube-side-content {
  color: white;
  font-weight: 600;
  text-align: center;
  padding: 1rem;
  writing-mode: vertical-rl;
  text-transform: uppercase;
  letter-spacing: 3px;
}

/* ============================================
   HOLOGRAPHIC GALLERY STYLE (CAROUSEL LAYOUT)
   ============================================ */
.holographic-gallery {
  padding: 2rem 0;
  max-width: 100%;
  overflow: hidden;
}

.holographic-carousel-wrapper {
  display: flex;
  gap: 2rem;
  align-items: stretch;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Main Large Image (50%) */
.holographic-main {
  flex: 0 0 calc(50% - 1rem);
  cursor: pointer;
  min-width: 0; /* Important for flexbox overflow */
}

.holographic-main-panel {
  height: 600px !important;
  width: 100%;
  max-width: 100%;
}

/* Right Side Grid Wrapper */
.holographic-grid-wrapper {
  flex: 0 0 calc(50% - 1rem);
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0 60px;
  min-width: 0; /* Important for flexbox overflow */
}

/* 2x2 Grid */
.holographic-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 1rem;
  height: 600px;
  width: 100%;
  max-width: 100%;
}

.holographic-frame {
  cursor: pointer;
  transition: transform 0.4s ease;
  position: relative;
  overflow: hidden; /* Prevent image overflow */
  min-width: 0; /* Important for grid overflow */
  min-height: 0; /* Important for grid overflow */
}

.holographic-frame:hover {
  transform: scale(1.05);
  z-index: 10;
}

/* Navigation Buttons */
.holographic-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.holographic-nav:hover {
  background: rgba(255, 255, 255, 1);
  border-color: rgba(0, 0, 0, 0.25);
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.holographic-nav-prev {
  left: -55px;
}

.holographic-nav-next {
  right: -55px;
}

.holographic-nav i {
  font-size: 1.3rem;
  color: #333;
}

/* Carousel Indicators */
.holographic-indicators {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 1rem;
}

.holographic-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.holographic-indicator:hover {
  background: rgba(0, 0, 0, 0.4);
  transform: scale(1.2);
}

.holographic-indicator.active {
  background: #333;
  transform: scale(1.3);
}

.holographic-panel {
  position: relative;
  height: 100%;
  width: 100%;
  max-width: 100%;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.holographic-scanlines {
  display: none;
}

.holographic-image {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  object-position: center;
  transition: all 0.4s ease;
  display: block;
}

.holographic-frame:hover .holographic-image {
  transform: scale(1.03);
}

.holographic-glitch {
  display: none;
}

.holographic-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(0, 0, 0, 0.85) 100%
  );
  display: flex;
  align-items: flex-end;
  padding: 2rem;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.holographic-main:hover .holographic-overlay {
  opacity: 1;
}

/* Thumb Overlay for Grid Items */
.holographic-thumb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.holographic-frame:hover .holographic-thumb-overlay {
  opacity: 1;
}

.holographic-thumb-overlay i {
  font-size: 2rem;
  color: #fff;
}

.holographic-data {
  color: #fff;
}

.holographic-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.holographic-desc {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 1rem;
}

.holographic-tag {
  display: inline-block;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Responsive για όλα τα νέα styles */
@media (max-width: 992px) {
  .holographic-carousel-wrapper {
    gap: 1rem;
    padding: 0 0.5rem;
  }

  .holographic-main {
    flex: 0 0 calc(50% - 0.5rem);
  }

  .holographic-main-panel {
    height: 450px !important;
  }

  .holographic-grid-wrapper {
    flex: 0 0 calc(50% - 0.5rem);
    padding: 0 45px;
  }

  .holographic-grid {
    height: 450px;
    gap: 0.6rem;
  }

  .holographic-nav {
    width: 38px;
    height: 38px;
  }

  .holographic-nav i {
    font-size: 1.1rem;
  }

  .holographic-nav-prev {
    left: -43px;
  }

  .holographic-nav-next {
    right: -43px;
  }

  .holographic-title {
    font-size: 1.3rem;
  }

  .holographic-desc {
    font-size: 0.85rem;
  }
}

@media (max-width: 768px) {
  .parallax-layer {
    width: calc(50% - 1rem);
  }

  .parallax-image {
    height: 250px;
  }

  .neon-grid {
    grid-template-columns: 1fr;
  }

  /* Liquid Carousel Responsive */
  .liquid-carousel-wrapper {
    padding: 0 50px;
  }

  .liquid-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  .liquid-blob {
    max-width: 250px;
    height: 300px;
  }

  .liquid-nav {
    width: 40px;
    height: 40px;
  }

  .liquid-nav i {
    font-size: 1.2rem;
  }

  .isometric-cube {
    width: 220px;
    height: 220px;
  }

  .cube-face {
    width: 220px;
    height: 220px;
  }

  .cube-front {
    transform: translateZ(110px);
  }

  .cube-top {
    transform: rotateX(90deg) translateZ(110px);
  }

  .cube-right {
    transform: rotateY(90deg) translateZ(110px);
  }

  /* Holographic Carousel Responsive */
  .holographic-carousel-wrapper {
    gap: 0.75rem;
    padding: 0 0.5rem;
  }

  .holographic-main {
    flex: 0 0 calc(48% - 0.375rem);
  }

  .holographic-main-panel {
    height: 350px !important;
  }

  .holographic-grid-wrapper {
    flex: 0 0 calc(52% - 0.375rem);
    padding: 0 38px;
  }

  .holographic-grid {
    height: 350px;
    gap: 0.5rem;
  }

  .holographic-nav {
    width: 32px;
    height: 32px;
  }

  .holographic-nav i {
    font-size: 0.95rem;
  }

  .holographic-nav-prev {
    left: -36px;
  }

  .holographic-nav-next {
    right: -36px;
  }

  .holographic-title {
    font-size: 1.1rem;
    letter-spacing: 1px;
  }

  .holographic-desc {
    font-size: 0.8rem;
    margin-bottom: 0.75rem;
  }

  .holographic-tag {
    font-size: 0.7rem;
    padding: 0.4rem 0.8rem;
  }

  .holographic-overlay {
    padding: 1.25rem;
  }
}

@media (max-width: 576px) {
  .parallax-layer {
    width: 100%;
  }

  /* Liquid Carousel Mobile - Keep 2x2 grid */
  .liquid-carousel-wrapper {
    padding: 0 45px;
  }

  .liquid-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .liquid-blob {
    max-width: 100%;
    height: 220px;
  }

  .liquid-nav {
    width: 35px;
    height: 35px;
  }

  .liquid-nav i {
    font-size: 1rem;
  }

  /* Holographic Mobile - Keep 50-50 layout but optimized */
  .holographic-gallery {
    padding: 1rem 0;
  }

  .holographic-carousel-wrapper {
    gap: 0.25rem;
    padding: 0;
  }

  .holographic-main {
    flex: 0 0 calc(48% - 0.125rem);
  }

  .holographic-main-panel {
    height: 240px !important;
  }

  .holographic-grid-wrapper {
    flex: 0 0 calc(52% - 0.125rem);
    padding: 0 28px;
  }

  .holographic-grid {
    height: 240px;
    gap: 0.25rem;
  }

  .holographic-nav {
    width: 28px;
    height: 28px;
  }

  .holographic-nav i {
    font-size: 0.85rem;
  }

  .holographic-nav-prev {
    left: -28px;
  }

  .holographic-nav-next {
    right: -28px;
  }

  .holographic-title {
    font-size: 0.95rem;
    letter-spacing: 0.5px;
    margin-bottom: 0.3rem;
  }

  .holographic-desc {
    font-size: 0.7rem;
    margin-bottom: 0.5rem;
    line-height: 1.2;
  }

  .holographic-tag {
    font-size: 0.6rem;
    padding: 0.3rem 0.6rem;
    letter-spacing: 0.5px;
  }

  .holographic-overlay {
    padding: 0.75rem;
  }

  .holographic-indicators {
    gap: 6px;
    margin-top: 0.75rem;
  }

  .holographic-indicator {
    width: 7px;
    height: 7px;
  }

  .holographic-thumb-overlay i {
    font-size: 1.3rem;
  }
}

/* Extra small devices */
@media (max-width: 480px) {
  .holographic-carousel-wrapper {
    gap: 0.2rem;
    padding: 0;
  }

  .holographic-main {
    flex: 0 0 calc(48% - 0.1rem);
  }

  .holographic-main-panel {
    height: 200px !important;
  }

  .holographic-grid-wrapper {
    flex: 0 0 calc(52% - 0.1rem);
    padding: 0 26px;
  }

  .holographic-grid {
    height: 200px;
    gap: 0.2rem;
  }

  .holographic-nav {
    width: 26px;
    height: 26px;
  }

  .holographic-nav i {
    font-size: 0.75rem;
  }

  .holographic-nav-prev {
    left: -27px;
  }

  .holographic-nav-next {
    right: -27px;
  }

  .holographic-title {
    font-size: 0.85rem;
    margin-bottom: 0.25rem;
  }

  .holographic-desc {
    font-size: 0.65rem;
    margin-bottom: 0.4rem;
  }

  .holographic-tag {
    font-size: 0.55rem;
    padding: 0.25rem 0.5rem;
  }

  .holographic-overlay {
    padding: 0.5rem;
  }

  .holographic-thumb-overlay i {
    font-size: 1.1rem;
  }

  .holographic-panel {
    border-width: 1.5px;
    border-radius: 6px;
  }
}
</style>
